<script setup lang="ts">
/**
 * H9 — Dark footer · link columns · giant wordmark · compact accordion
 * Figma RedisignHomePage `1:1870` / compact `1:1952`
 */
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import fairLogoWordmarkUrl from '@/ui/icons/brand/fair-logo-wordmark.svg'
import { FairIcon } from '@/ui/primitives/FairIcon'
import {
  FAIR_HOME_V2_FOOTER_COPYRIGHT_YEAR,
  FAIR_HOME_V2_FOOTER_SECTIONS,
  isFairHomeV2FooterSocialSection,
  type FairHomeV2FooterLink,
  type FairHomeV2FooterSection,
} from './fair-home-v2-footer.data'
import './fair-home-v2-footer.css'

const props = withDefaults(
  defineProps<{
    compact?: boolean
  }>(),
  {
    compact: false,
  },
)

const { t } = useI18n()

const openSections = reactive<Record<string, boolean>>({})

const sections = computed(() => FAIR_HOME_V2_FOOTER_SECTIONS)

const rootClass = computed(() => ({
  'fair-home-v2-footer': true,
  'fair-home-v2-footer--compact': props.compact,
  'fair-home-v2-footer--desktop': !props.compact,
}))

const desktopNavSections = computed(() =>
  sections.value.filter((section) => !isFairHomeV2FooterSocialSection(section)),
)

const socialSection = computed(() =>
  sections.value.find((section) => isFairHomeV2FooterSocialSection(section)),
)

function sectionTitleKey(sectionId: string): string {
  return `fair_home_v2.footer.sections.${sectionId}.title`
}

function linkLabelKey(sectionId: string, linkId: string): string {
  return `fair_home_v2.footer.sections.${sectionId}.${linkId}`
}

function isSectionOpen(sectionId: string): boolean {
  return Boolean(openSections[sectionId])
}

function toggleSection(sectionId: string) {
  openSections[sectionId] = !openSections[sectionId]
}

function isRouterLink(link: FairHomeV2FooterLink): link is FairHomeV2FooterLink & {
  to: RouteLocationRaw
} {
  return link.to != null
}

function isExternalLink(link: FairHomeV2FooterLink): boolean {
  return Boolean(link.external && link.href)
}

function linkRel(link: FairHomeV2FooterLink): string | undefined {
  return isExternalLink(link) ? 'noopener noreferrer' : undefined
}

function linkTarget(link: FairHomeV2FooterLink): string | undefined {
  return isExternalLink(link) ? '_blank' : undefined
}

function isCapsLink(section: FairHomeV2FooterSection, link: FairHomeV2FooterLink): boolean {
  return isFairHomeV2FooterSocialSection(section)
}

function sectionLinksClass(section: FairHomeV2FooterSection): Record<string, boolean> {
  return {
    'fair-home-v2-footer__links': true,
    'fair-home-v2-footer__links--row': section.layout === 'row',
  }
}
</script>

<template>
  <footer
    :class="rootClass"
    aria-label="site footer"
  >
    <div
      v-if="!compact"
      class="fair-home-v2-footer__nav"
    >
      <div class="fair-home-v2-footer__columns">
        <div
          v-for="section in desktopNavSections"
          :key="section.id"
          class="fair-home-v2-footer__column"
        >
          <h2 class="fair-home-v2-footer__column-title">
            {{ t(sectionTitleKey(section.id)) }}
          </h2>
          <ul :class="sectionLinksClass(section)">
            <li
              v-for="link in section.links"
              :key="link.id"
            >
              <RouterLink
                v-if="isRouterLink(link)"
                :to="link.to"
                class="fair-home-v2-footer__link"
                :class="{
                  'fair-home-v2-footer__link--caps': isCapsLink(section, link),
                }"
              >
                {{ t(linkLabelKey(section.id, link.id)) }}
              </RouterLink>
              <a
                v-else-if="link.href"
                :href="link.href"
                class="fair-home-v2-footer__link"
                :class="{
                  'fair-home-v2-footer__link--caps': isCapsLink(section, link),
                }"
                :target="linkTarget(link)"
                :rel="linkRel(link)"
              >
                {{ t(linkLabelKey(section.id, link.id)) }}
              </a>
              <button
                v-else
                type="button"
                class="fair-home-v2-footer__link"
                :class="{
                  'fair-home-v2-footer__link--caps': isCapsLink(section, link),
                }"
              >
                {{ t(linkLabelKey(section.id, link.id)) }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="socialSection"
        class="fair-home-v2-footer__column fair-home-v2-footer__column--social"
      >
        <h2 class="fair-home-v2-footer__column-title">
          {{ t(sectionTitleKey(socialSection.id)) }}
        </h2>
        <ul :class="sectionLinksClass(socialSection)">
          <li
            v-for="link in socialSection.links"
            :key="link.id"
          >
            <a
              v-if="link.href"
              :href="link.href"
              class="fair-home-v2-footer__link fair-home-v2-footer__link--caps"
              :target="linkTarget(link)"
              :rel="linkRel(link)"
            >
              {{ t(linkLabelKey(socialSection.id, link.id)) }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-else
      class="fair-home-v2-footer__accordion"
    >
      <div
        v-for="section in sections"
        :key="section.id"
        class="fair-home-v2-footer__accordion-item"
        :class="{
          'fair-home-v2-footer__accordion-item--open': isSectionOpen(section.id),
        }"
      >
        <button
          type="button"
          class="fair-home-v2-footer__accordion-trigger"
          :aria-expanded="isSectionOpen(section.id)"
          @click="toggleSection(section.id)"
        >
          <span class="fair-home-v2-footer__accordion-title">
            {{ t(sectionTitleKey(section.id)) }}
          </span>
          <FairIcon
            class="fair-home-v2-footer__accordion-icon"
            name="arrow-down"
            :size="20"
            aria-hidden="true"
          />
        </button>

        <div
          class="fair-home-v2-footer__accordion-panel-wrap"
          :class="{
            'fair-home-v2-footer__accordion-panel-wrap--open': isSectionOpen(
              section.id,
            ),
          }"
          :inert="!isSectionOpen(section.id) || undefined"
        >
          <ul
            class="fair-home-v2-footer__accordion-panel"
            :aria-hidden="!isSectionOpen(section.id)"
          >
            <li
              v-for="link in section.links"
              :key="link.id"
            >
              <RouterLink
                v-if="isRouterLink(link)"
                :to="link.to"
                class="fair-home-v2-footer__accordion-link"
                :class="{
                  'fair-home-v2-footer__accordion-link--caps': isCapsLink(
                    section,
                    link,
                  ),
                }"
              >
                {{ t(linkLabelKey(section.id, link.id)) }}
              </RouterLink>
              <a
                v-else-if="link.href"
                :href="link.href"
                class="fair-home-v2-footer__accordion-link"
                :class="{
                  'fair-home-v2-footer__accordion-link--caps': isCapsLink(
                    section,
                    link,
                  ),
                }"
                :target="linkTarget(link)"
                :rel="linkRel(link)"
              >
                {{ t(linkLabelKey(section.id, link.id)) }}
              </a>
              <button
                v-else
                type="button"
                class="fair-home-v2-footer__accordion-link"
                :class="{
                  'fair-home-v2-footer__accordion-link--caps': isCapsLink(
                    section,
                    link,
                  ),
                }"
              >
                {{ t(linkLabelKey(section.id, link.id)) }}
              </button>
            </li>
          </ul>
        </div>

        <div
          class="fair-home-v2-footer__accordion-divider"
          aria-hidden="true"
        />
      </div>
    </div>

    <div class="fair-home-v2-footer__brand">
      <div class="fair-home-v2-footer__wordmark-wrap">
        <img
          class="fair-home-v2-footer__wordmark"
          :src="fairLogoWordmarkUrl"
          alt="FAIR"
          width="884"
          height="192"
          loading="lazy"
          decoding="async"
        >
      </div>
      <p class="fair-home-v2-footer__copyright">
        {{ t('fair_home_v2.footer.copyright', { year: FAIR_HOME_V2_FOOTER_COPYRIGHT_YEAR }) }}
      </p>
    </div>
  </footer>
</template>
